import React from 'react'
import { Stack, Text } from '@mantine/core'
import { useTheme } from '../../../components/ThemeProvider'

interface CourtCasesOverviewProps {
  data: any
}

export default function CourtCasesOverview({ data }: CourtCasesOverviewProps) {
  const theme = useTheme()

  const civilCases = data?.court_case_info?.civil ?? []
  const criminalCases = data?.court_case_info?.criminal ?? []
  const otherCases = data?.court_case_info?.other ?? []

  const allCases = [...civilCases, ...criminalCases, ...otherCases]
  const totalCases = allCases.length

  const calculateTotalAmount = () => {
    let total = 0

    civilCases.forEach((item: any) => {
      const caseObj = item?.caseObj || item
      const claimAmount = Number(caseObj?.claimAmount) || 0
      const closingAmount = Number(caseObj?.closingAmount) || 0
      total += Math.max(claimAmount, closingAmount)
    })

    criminalCases.forEach((item: any) => {
      const caseObj = item?.caseObj || item
      const crimeAmount = Number(caseObj?.crimeAmount) || 0
      const compensationAmount = Number(caseObj?.judgmentCompensation) || 0
      total += crimeAmount + compensationAmount
    })

    return total
  }

  const countPendingCases = () => {
    return allCases.filter((item: any) => {
      const caseObj = item?.caseObj || item
      const status = caseObj?.caseStatus || ''
      return status !== '' && status !== '已结案' && status !== '结案'
    }).length
  }

  const totalAmount = calculateTotalAmount()
  const pendingCases = countPendingCases()

  const formatAmount = (amount: number) => {
    if (amount === 0) return '¥0'
    if (amount >= 10000) {
      return `¥${(amount / 10000).toFixed(1)}万`
    }
    return `¥${amount.toFixed(0)}`
  }

  const caseTypeBreakdown = `民事${civilCases.length}件 / 刑事${criminalCases.length}件${otherCases.length > 0 ? ` / 其他${otherCases.length}件` : ''}`

  return (
    <div className="mt-2">
      <Stack gap="md">
        <div className="flex flex-col gap-2">
          <Text size="sm" fw={600} style={{ color: theme.accentTextColor }}>
            案件风险概览
          </Text>
          <Text size="xs" className="text-gray-500">
            汇总关联案件的整体风险情况,包括案件规模、涉案金额及未结案风险敞口。
          </Text>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          <SummaryItem label="案件总数" value={`${totalCases} 件`} subtitle={caseTypeBreakdown} />
          <SummaryItem label="涉案总金额" value={formatAmount(totalAmount)} subtitle="起诉标的 + 刑事涉案金额" />
          <SummaryItem
            label="未结案数量"
            value={`${pendingCases} 件`}
            subtitle={pendingCases > 0 ? '存在潜在风险' : '无风险敞口'}
            highlight={pendingCases > 0}
          />
        </div>
      </Stack>
    </div>
  )
}

interface SummaryItemProps {
  label: string
  value: string
  subtitle?: string
  highlight?: boolean
}

function SummaryItem({ label, value, subtitle, highlight }: SummaryItemProps) {
  const theme = useTheme()
  return (
    <div
      className="rounded-lg border px-4 py-3"
      style={{ borderColor: theme.surfaceBorderColor, backgroundColor: theme.surfaceColor }}
    >
      <Text size="xs" className="text-gray-500">
        {label}
      </Text>
      <Text size="sm" fw={600} style={{ marginTop: '6px', color: highlight ? '#F59E0B' : theme.primaryColor }}>
        {value}
      </Text>
      {subtitle && (
        <Text size="xs" className="text-gray-400" style={{ marginTop: '4px' }}>
          {subtitle}
        </Text>
      )}
    </div>
  )
}
